import * as React from 'react';
import { connect } from 'react-redux';
import { AppState } from '@om-fe/core/core/types';
import { FilteredBettingOffers, FilteredOutcomes, MatchListStoreModel } from '../match-list/utils/match-list-models';
import BaseOddsButton from '@om-fe/core/components/odds-button/base-odds-button';
import OddsViewModel from '@om-fe/core/core/viewmodels/odds';
import {
    marketsIdsToDisplayOddsParamBetweenOdds,
    marketsIdsToDisplayOddsParamFor,
    OVER_STRING_CONSTANT,
    UNDER_STRING_CONSTANT,
} from '../match-list/utils/match-list.constants';
import getSlice from '@om-fe/core/utils/get-slice';
import getConfig, { OMFE2ConfigModel } from '@om-fe/core/utils/config';
import { BettingOfferStatus } from '@om-fe/models';

const CURRENT_ODDS_DIGITS = 2;

export interface OddsButtonWrapperPublicProps {
    readonly sliceKey: string;
    readonly outcomeId?: string;
    readonly bettingOfferId?: string;
    readonly bettingOfferList?: string[];
    readonly eventId: string;
    readonly showOddsBar?: boolean;
}

interface OddsButtonWrapperStoreProps {
    readonly bettingOffer?: FilteredBettingOffers;
    readonly outcome?: FilteredOutcomes;
    readonly validBettingOfferIds?: string[];
    readonly bettingOfferRankById?: Record<string, number>;
}

export type OddsButtonWrapperProps = OddsButtonWrapperPublicProps & OddsButtonWrapperStoreProps;

function getRankHighlightBettingOfferRankById(
    filteredBettingOffers: MatchListStoreModel['filteredBettingOffers'],
    bettingOfferList?: string[],
): Record<string, number> | undefined {
    if (!bettingOfferList) return;

    const validOffers = bettingOfferList.reduce<{ id: string; odds: number }[]>((result, key) => {
        if (!key) {
            return result;
        }

        const offer = filteredBettingOffers[key];
        if (!offer || typeof offer.odds !== 'number') {
            return result;
        }

        result.push({
            id: offer.id,
            odds: Number(offer.odds.toFixed(CURRENT_ODDS_DIGITS)),
        });

        return result;
    }, []);

    if (!validOffers.length) return;

    validOffers.sort((left, right) => left.odds - right.odds);

    const rankById: Record<string, number> = {};
    let lastOdds: number | undefined;
    let rank = 0;

    for (const { id, odds } of validOffers) {
        if (lastOdds === undefined || odds !== lastOdds) {
            rank += 1;
            lastOdds = odds;
        }

        rankById[id] = rank;
    }

    return rankById;
}

export class OddsButtonWrapperComponent extends React.PureComponent<OddsButtonWrapperProps> {

    shouldShowParameter(bettingTypeId: string | undefined): boolean | undefined {
        if (!bettingTypeId) {
            return undefined;
        }

        return marketsIdsToDisplayOddsParamFor.includes(bettingTypeId);
    }

    getOutsideParameter(
        bettingTypeId: string | undefined,
        paramFloat1: number | undefined,
        headerNameKey: string | undefined,
    ) {
        const { markets }: OMFE2ConfigModel = getConfig();
        const overUnderConstant: string = markets && markets.overUnderSwitch
                                          ? OVER_STRING_CONSTANT
                                          : UNDER_STRING_CONSTANT;

        if (
            bettingTypeId
            && marketsIdsToDisplayOddsParamBetweenOdds.includes(bettingTypeId)
            && headerNameKey === overUnderConstant
        ) {
            return (
                <span
                    key={paramFloat1}
                    className="OddsParameter"
                >
                    {paramFloat1}
                </span>
            );
        }

        return undefined;
    }

    getButtonText(
        bettingTypeId: string | undefined,
        translatedName: string,
        paramFloat1: number | undefined,
    ) {

        return (bettingTypeId && marketsIdsToDisplayOddsParamBetweenOdds.includes(bettingTypeId)
            ? translatedName.replace(` ${paramFloat1}`, '')
            : translatedName
        )
    }

    parseParameter(parameter: number): string | number {
        const { bettingOffer } = this.props;
        const bettingTypeId: string | undefined = bettingOffer && bettingOffer.bettingTypeId;

        if (!bettingTypeId || !marketsIdsToDisplayOddsParamFor.includes(bettingTypeId)) {
            return parameter;
        }

        return parameter > 0 ? `+${parameter}` : `${parameter}`;
    }

    render() {
        const { bettingOffer, outcome, eventId, showOddsBar, validBettingOfferIds, bettingOfferRankById } = this.props;
        const displayButtonText: boolean = getConfig().matchListConfig?.displayButtonText ?? true;

        if (!outcome) {
            return null;
        }

        const { id: outcomeId, paramFloat1, headerNameKey, shortTranslatedName, translatedName } = outcome;
        const bettingTypeId: string | undefined = bettingOffer && bettingOffer.bettingTypeId;

        const item: OddsViewModel = {
            odds: bettingOffer?.odds,
            bettingOfferId: bettingOffer?.id,
            outcome: outcomeId,
            text: displayButtonText ? this.getButtonText(bettingTypeId, translatedName, paramFloat1) : '',
            shortText: displayButtonText ? this.getButtonText(bettingTypeId, shortTranslatedName, paramFloat1) : '',
            removed: !!bettingOffer && !bettingOffer.isAvailable,
            suspended: bettingOffer?.statusId === BettingOfferStatus.Suspended,
            parameter: typeof paramFloat1 === 'undefined' ? paramFloat1 : this.parseParameter(paramFloat1),
            lastChangedTime: bettingOffer?.lastChangedTime || 0,
        };

        return (
            <>
                {this.getOutsideParameter(bettingTypeId, paramFloat1, headerNameKey)}
                <BaseOddsButton
                    eventId={eventId}
                    item={item}
                    showParameter={!item.text && this.shouldShowParameter(bettingTypeId)}
                    showOddsBar={showOddsBar}
                    validBettingOfferIds={validBettingOfferIds}
                    bettingOfferRankById={bettingOfferRankById}
                />
            </>

        );
    }
}

const OddsButtonWrapper: React.ComponentType<OddsButtonWrapperPublicProps> = connect<OddsButtonWrapperStoreProps, {}, OddsButtonWrapperPublicProps>(
        (state: AppState, props: OddsButtonWrapperPublicProps): OddsButtonWrapperStoreProps => {
            const { sliceKey, outcomeId, bettingOfferId, bettingOfferList } = props;
            const slice: MatchListStoreModel | undefined = getSlice<MatchListStoreModel>(state, sliceKey);

            if (!slice) {
                return {};
            }

            const bettingOffer: FilteredBettingOffers | undefined = bettingOfferId ?
                slice.filteredBettingOffers[bettingOfferId] : undefined;

            const validBettingOfferIds: string[] | undefined = bettingOfferList
                ?.map((item) => slice.filteredBettingOffers[item])
                .filter((offer: FilteredBettingOffers) => !!offer && typeof offer.odds === 'number')
                .reduce((result: string[], current) => {
                    if (!result.length) {
                      result.push(current.id);
                    } else {
                      const currentOdds = current.odds;
                      const minOdds = slice.filteredBettingOffers[result[0]].odds;

                      if (typeof currentOdds !== 'number' || typeof minOdds !== 'number') {
                        return result;
                      }
                
                      if (currentOdds.toFixed(CURRENT_ODDS_DIGITS) < minOdds.toFixed(CURRENT_ODDS_DIGITS)) {
                        return [current.id];
                      } else if (currentOdds.toFixed(CURRENT_ODDS_DIGITS) === minOdds.toFixed(CURRENT_ODDS_DIGITS)) {
                        result.push(current.id);
                      }
                    }
                    
                    return result;
                }, []);

            const isRankHighlightEnabled: boolean = !!getConfig().enabledOddsBarRankHighlight;
            const bettingOfferRankById: Record<string, number> | undefined = isRankHighlightEnabled
                ? getRankHighlightBettingOfferRankById(slice.filteredBettingOffers, bettingOfferList)
                : undefined;

            const outcome: FilteredOutcomes | undefined = outcomeId ?
                slice.filteredOutcomes[outcomeId] : undefined;

            return {
                bettingOffer: bettingOffer || undefined,
                outcome,
                validBettingOfferIds,
                bettingOfferRankById,
            };
        },
    )(OddsButtonWrapperComponent);

export default OddsButtonWrapper;
